import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useState } from "react";
import { DesktopNav } from "./header/DesktopNav";
import { MobileNav } from "./header/MobileNav";
import { HeaderWallet } from "./header/HeaderWallet";
import { MenuItem } from "./header/types";
import { Badge } from "./ui/badge";
import { Bell } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  const isAuthPage = location.pathname === "/login" || location.pathname === "/register";

  // Query to get unread notifications count
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["unreadNotificationsCount", user?.id],
    queryFn: async () => {
      if (!user) return 0;
      const { data } = await supabase
        .from("notifications")
        .select("id", { count: "exact" })
        .eq("user_id", user.id)
        .eq("read", false);
      return data?.length || 0;
    },
    enabled: !!user,
  });

  const menuItems: MenuItem[] = [
    { label: "Home", path: "/" },
    { label: "Available Matches", path: "/matches" },
    { label: "Post Match", path: "/post-match" },
    { label: "Profile", path: "/profile" },
    { 
      label: "Notifications", // Keep label for mobile menu
      path: "/notifications",
      icon: (
        <div className="relative md:block hidden">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="secondary" 
              className="absolute -top-2 -right-2 h-4 w-4 p-0 flex items-center justify-center text-xs"
            >
              {unreadCount}
            </Badge>
          )}
        </div>
      )
    },
  ].filter(item => {
    if (isAuthPage) return false;
    return location.pathname !== item.path && 
           !(user?.id && item.path === "/profile" && location.pathname === `/profile/${user.id}`);
  });

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const handleNavigation = (path: string) => {
    if (path === "/profile" && user) {
      navigate(`/profile/${user.id}`);
    } else {
      navigate(path);
    }
    setIsOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white/60 backdrop-blur-md supports-[backdrop-filter]:bg-white/60 dark:bg-background/60 z-50 border-b shadow-sm">
      <div className="container flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
        <button 
          onClick={() => handleNavigation("/")} 
          className="text-xl sm:text-2xl font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
        >
          <img 
            src="/LogoPorteroYa.svg" 
            alt="PorteroYa Logo" 
            className="h-14 w-auto"
          />
          <span className="text-primary">PorteroYa</span>
        </button>

        {!isAuthPage && (
          <div className="flex items-center gap-4">
            <div className="hidden md:block">
              {user && <HeaderWallet />}
            </div>
            <DesktopNav 
              user={user}
              menuItems={menuItems}
              handleNavigation={handleNavigation}
              handleSignOut={handleSignOut}
            />
            <div className="md:hidden flex items-center gap-2">
              {user && <HeaderWallet />}
              <MobileNav 
                user={user}
                menuItems={menuItems}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                handleNavigation={handleNavigation}
                handleSignOut={handleSignOut}
              />
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
